import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getModelsTypeFilters } from "../../services";
import { apiInstance } from "../../utils";
import {
  getStoredSearchQuery,
  getStoredSearchType,
  removeSearchQuery,
  storeSearchQuery,
  storeSearchType,
} from "../../utils/_localStorage";
import { ErrorMessage } from "../../utils/classes/_errorMessage";
import { getAppliedFilters } from "../selectors";
import { addError, startLoading, stopLoading } from "./_uiReducer";

export const SEARCH_TYPES = {
  all: {
    id: "all",
    collectionType: "all",
    title: "Tudo",
    path: "/pesquisa/generica",
    filters: "/filtros/generica",
    order: 1,
    personFilter: null,
  },
  jurisprudencia: {
    id: "jurisprudencia",
    collectionType: "Jurisprudencia",
    title: "Jurisprudência",
    path: "/pesquisa/jurisprudencia",
    filters: "/filtros/jurisprudencia",
    order: 2,
    personFilter: {
      param: "relator",
      label: "Relator",
    },
  },
  legislacao: {
    id: "legislacao",
    collectionType: "Legislacao",
    title: "Legislação",
    path: "/pesquisa/legislacao",
    filters: "/filtros/legislacao",
    order: 3,
    personFilter: null,
  },
  doutrinas: {
    id: "doutrinas",
    collectionType: "Doutrinas",
    title: "Doutrina",
    path: "/pesquisa/doutrina",
    filters: "/filtros/doutrina",
    order: 4,
    personFilter: {
      param: "autor",
      label: "Autor",
    },
  },
  modelos: {
    id: "modelos",
    collectionType: "Modelos",
    title: "Modelos",
    path: "/pesquisa/modelos",
    // filters: "/filtros/modelos
    filters: "/filtros/modelos/peticoes",
    order: 6,
    personFilter: null,
  },
};

const EMPTY_ITEM = { total: 0, results: [], currentPage: 1, totalPages: 0 };
const DEFAULT_PERIOD = {
  period: "always",
  startDate: null,
  endDate: null,
};
const EMPTY_SEARCH_RESULTS = {
  jurisprudencia: { ...EMPTY_ITEM },
  legislacao: { ...EMPTY_ITEM },
  doutrinas: { ...EMPTY_ITEM },
  modelos: { ...EMPTY_ITEM },
};

const getInitialQuery = () => {
  try {
    const storedQuery = getStoredSearchQuery();
    return storedQuery || "";
  } catch (error) {
    return "";
  }
};

const getInitialSearchType = () => {
  try {
    const storedType = getStoredSearchType();
    if (storedType && SEARCH_TYPES[storedType]) {
      return SEARCH_TYPES[storedType];
    }
    return SEARCH_TYPES.all;
  } catch (error) {
    return SEARCH_TYPES.all;
  }
};

const initialState = {
  query: getInitialQuery(),
  currentInputQuery: "",
  sortBy: "relevancia",
  appliedFilters: {
    jurisprudencia: [],
    legislacao: [],
    doutrinas: [],
    modelos: [],
  },
  searchFilters: {
    jurisprudencia: [],
    legislacao: [],
    doutrinas: [],
    modelos: [],
  },
  searchModelType: "all",
  modelTypeSearchFilter: {
    peticoes: [],
    sentencas: [],
    despachos: [],
    contratos: [],
    pareceres: [],
    denuncias: [],
  },
  modelTypeAppliedFilters: {
    peticoes: [],
    sentencas: [],
    despachos: [],
    contratos: [],
    pareceres: [],
    denuncias: [],
  },
  searchPeriod: {
    jurisprudencia: { ...DEFAULT_PERIOD },
    legislacao: { ...DEFAULT_PERIOD },
    doutrinas: { ...DEFAULT_PERIOD },
    modelos: { ...DEFAULT_PERIOD },
  },
  searchType: getInitialSearchType(),
  searchResults: { ...EMPTY_SEARCH_RESULTS },
  searchResultsType: SEARCH_TYPES.all.id,
  filtersLoading: false,
  selectedPersonFilter: {
    jurisprudencia: "",
    doutrinas: "",
    modelos: "",
    legislacao: "",
  },
  isLoading: false,
};

export const fetchFilters = createAsyncThunk(
  "search/fetchFilters",
  async (params, { dispatch }) => {
    try {
      const {
        searchType: { id = null, filters = "/pesquisa/generica/filtros" },
      } = params || {};

      const { data } = await apiInstance.get(filters);
      return { data, searchType: id };
    } catch (error) {
      throw error;
    }
  },
);

export const fetchModelTypeFilters = createAsyncThunk(
  "search/fetchModelTypeFilters",
  async (modelType, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();

      const cached = state.searchContext.modelTypeSearchFilter[modelType];
      if (cached && cached.length > 0) {
        return cached;
      }

      const response = await getModelsTypeFilters({ searchType: modelType });

      dispatch(
        setModelTypeSearchFilter({
          modelType,
          filters: response.filters,
        }),
      );

      return response.filters;
    } catch (err) {
      return rejectWithValue("Erro ao buscar filtros");
    }
  },
);

export const doSearch = createAsyncThunk(
  "search/execute",
  /**
   * @param {{
   *  query?: string;
   *  searchType?: { collectionType?: string; id?: string; path?: string };
   *  page?: number;
   *  requestParams?: Record<string, any>;
   * }} params
   */
  async (params, { dispatch, getState }) => {
    try {
      const state = getState();
      const appliedFilters = getAppliedFilters(state);
      const filters = {};

      const {
        query = null,
        searchType: {
          collectionType = "all",
          id = null,
          path = "/pesquisa",
        } = {},
        page = 1,
        requestParams = null,
      } = params || {};

      const { sortBy } = state.searchContext;

      if (id === "all") {
        dispatch(setQueryValue(query || ""));
        dispatch(startLoading());

        const { data } = await apiInstance.get("/pesquisa", {
          params: {
            q: query,
            collectionType: "all",
            page: 1,
            limit: 10,
            sort: sortBy,
          },
        });

        dispatch(stopLoading());
        dispatch(setSearchResultsType("all"));

        return { data, searchType: "all", page: 1 };
      }

      if (id === "modelos") {
        const {
          searchModelType,
          modelTypeSearchFilter,
          modelTypeAppliedFilters,
        } = state.searchContext;

        if (searchModelType && searchModelType !== "all") {
          filters.tipoModelo = searchModelType;
        }

        const appliedValues = modelTypeAppliedFilters[searchModelType] || [];

        const modelFilters = modelTypeSearchFilter[searchModelType] || [];

        modelFilters.forEach(({ id: filterId, options = [] }) => {
          const selected = options
            .filter((opt) => opt.checked && appliedValues.includes(opt.label))
            .map((opt) => opt.label);

          if (selected.length) {
            filters[filterId] = selected.join(",");
          }
        });
      }

      appliedFilters[id].forEach(({ id = null, values = [] }) => {
        if (id && values.length) {
          filters[id] = values.join(",");
        }
      });

      const { period, startDate, endDate } =
        state.searchContext.searchPeriod[id] || {};

      const computedParams = {
        q: query,
        limit: 10,
        page,
        sort: sortBy,
        ...(collectionType && { collectionType }),
        ...(Object.keys(filters).length && filters),
      };

      const { personFilter } = state.searchContext.searchType;
      const personValue = state.searchContext.selectedPersonFilter[id];

      if (personFilter && personValue) {
        computedParams[personFilter.param] = personValue;
      }

      if (period && period !== "always") {
        if (startDate) computedParams.dataInicio = startDate;
        if (endDate) computedParams.dataFim = endDate;
      }

      const ignoredParams = ["q", "limit", "page", "sort", "collectionType"];

      const hasQuery = !!computedParams.q?.trim();

      const hasExtraParams = Object.keys(computedParams).some(
        (key) => !ignoredParams.includes(key),
      );

      if (!hasQuery && !hasExtraParams) {
        const errorMessage = new ErrorMessage({
          message:
            "Informe um termo de pesquisa ou aplique ao menos um filtro.",
          title: "Pesquisa Inválida",
          variant: "warning",
        });

        dispatch(addError(errorMessage));
        dispatch(stopLoading());

        throw new Error("Pesquisa sem critérios.");
      }

      dispatch(setQueryValue(query || ""));

      dispatch(startLoading());

      const finalParams = requestParams || computedParams;

      const { data = null } = await apiInstance.get(path, {
        params: finalParams,
      });

      dispatch(stopLoading());
      dispatch(setSearchResultsType(id));

      return { data, searchType: id, page: finalParams.page || page };
    } catch (err) {
      dispatch(stopLoading());
      const errorMessage = new ErrorMessage({
        message:
          "Erro ao realizar a pesquisa. Tente novamente ou contate o suporte.",
        title: "Erro de Pesquisa",
        variant: "danger",
      });
      if (err.response.status === 401) return;
      dispatch(addError(errorMessage));
      throw err;
    }
  },
);

const updateOptions = (curOption, newValue) => {
  if (curOption.type === "group" && curOption?.options?.length) {
    curOption.options.forEach((subOption) => {
      updateOptions(subOption, newValue);
    });
    curOption.checked = curOption.options.every((o) => o.checked);
  } else {
    curOption.checked = newValue;
  }
};

const processOptions = ({ options = [], option }) => {
  options.forEach((curOption) => {
    if (curOption.id === option.id) {
      const newValue = !curOption.checked;
      updateOptions(curOption, newValue);
    } else {
      processOptions({ options: curOption.options, option });
    }
  });
};

const clearOptions = ({ options = [] }) => {
  options?.forEach((curOption) => {
    if (curOption.type === "option") {
      curOption.checked = false;
    } else {
      clearOptions({ options: curOption.options });
    }
  });
};

const uncheckOption = (curOption, optionId) => {
  if (curOption.type === "group" && curOption.options?.length) {
    curOption.options.forEach((subOption) => {
      uncheckOption(subOption, optionId);
    });
    curOption.checked = curOption.options.every((o) => o.checked);
  } else if (curOption.type === "option" && curOption.id === optionId) {
    curOption.checked = false;
  }
};

const searchSlice = createSlice({
  name: "searchContext",
  initialState,
  reducers: {
    clearSearch: (state) => {
      const preservedQuery = state.query;

      Object.keys(initialState).forEach((key) => {
        if (key === "query") return;

        const initialValue = initialState[key];
        state[key] = Array.isArray(initialValue)
          ? [...initialValue]
          : typeof initialValue === "object" && initialValue !== null
            ? { ...initialValue }
            : initialValue;
      });

      state.query = preservedQuery;
    },
    setQueryValue: (state, { payload }) => {
      const newValue = payload.trim();
      state.query = newValue;
      if (!!newValue) storeSearchQuery(newValue);
      else removeSearchQuery();
    },
    setCurrentInputQuery: (state, { payload }) => {
      state.currentInputQuery = payload || "";
    },
    setSearchType: (state, { payload }) => {
      const type = typeof payload === "string" ? payload.toLowerCase() : null;
      const newSearchType = SEARCH_TYPES[type] || SEARCH_TYPES.all;

      state.searchType = newSearchType;
      storeSearchType(newSearchType.id);
    },
    setSearchResultsType: (state, { payload }) => {
      state.searchResultsType = payload || "all";
    },
    changePage: (state, { payload }) => {
      const { searchType, page } = payload;
      if (state.searchResults[searchType]) {
        state.searchResults[searchType].currentPage = page;
      }
    },
    resetCurrentFilters: (state) => {
      const currentType = state.searchType.id;
      const currentModelType = state.searchModelType;

      state.searchFilters[currentType]?.forEach(({ options = [] }) => {
        clearOptions({ options });
      });

      if (state.modelTypeSearchFilter[currentModelType]) {
        state.modelTypeSearchFilter[currentModelType].forEach(
          ({ options = [] }) => {
            clearOptions({ options });
          },
        );
      }
    },
    setAppliedFilters: (state, { payload: { appliedFilters = [] } }) => {
      state.appliedFilters[state.searchType.id] = appliedFilters;
    },
    updateFilters: (state, { payload: { option = null } }) => {
      state.searchFilters[state.searchType.id]?.forEach(({ options = [] }) => {
        processOptions({ options, option });
      });
    },
    setSortBy: (state, { payload }) => {
      state.sortBy = payload || "relevancia";
    },
    setSearchPeriod: (state, { payload }) => {
      const { searchType, period, startDate, endDate } = payload;

      if (state.searchPeriod[searchType]) {
        state.searchPeriod[searchType] = { period, startDate, endDate };
      }
    },
    uncheckFilter: (state, { payload: { optionId } }) => {
      state.searchFilters[state.searchType.id]?.forEach(({ options = [] }) => {
        options.forEach((curOption) => {
          uncheckOption(curOption, optionId);
        });
      });
    },
    setSelectedPersonFilter: (state, { payload }) => {
      const { searchType, value } = payload;
      state.selectedPersonFilter[searchType] = value;
    },
    setSearchModelType(state, action) {
      state.searchModelType = action.payload;
    },

    setModelTypeSearchFilter(state, action) {
      const { modelType, filters } = action.payload;
      state.modelTypeSearchFilter[modelType] = filters;
    },

    setModelTypeAppliedFilter(state, action) {
      const { modelType, filters } = action.payload;
      state.modelTypeAppliedFilters[modelType] = filters;
    },
    clearAllModelTypeAppliedFilters(state) {
      const currentModelType = state.searchModelType;
      if (state.modelTypeSearchFilter[currentModelType]) {
        state.modelTypeSearchFilter[currentModelType].forEach(
          ({ options = [] }) => {
            clearOptions({ options });
          },
        );
      }
      Object.keys(state.modelTypeAppliedFilters).forEach((key) => {
        state.modelTypeAppliedFilters[key] = [];
      });

      state.searchModelType = "all";
    },
    clearModelTypeAppliedFilters(state, action) {
      const modelType = action.payload;
      state.modelTypeAppliedFilters[modelType] = [];
    },
    updateModelTypeFilters: (
      state,
      { payload: { option = null, modelType } },
    ) => {
      const filters = state.modelTypeSearchFilter[modelType];
      if (!filters) return;

      filters.forEach(({ options = [] }) => {
        processOptions({ options, option });
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(doSearch.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        doSearch.fulfilled,
        (
          state,
          { payload: { data = {}, searchType = null, page = 1 } = {} },
        ) => {
          state.isLoading = false;

          if (searchType === "all") {
            Object.entries(data).forEach(([key, value]) => {
              state.searchResults[key] = {
                total: value?.total || 0,
                results: value?.results || [],
                currentPage: 1,
                totalPages: value?.totalPages || 1,
              };
            });
            return;
          }

          const total = data.pagination?.totalResults || data.total || 0;

          state.searchResults[searchType] = {
            total,
            results: data.results || data.resultados || [],
            currentPage: page,
            totalPages: data.pagination?.totalPages || Math.ceil(total / 10),
          };

          state.searchResultsType = searchType;
        },
      )
      .addCase(doSearch.rejected, (state) => {
        state.isLoading = false;
      });
    builder
      .addCase(fetchFilters.pending, (state) => {
        state.filtersLoading = true;
      })
      .addCase(
        fetchFilters.fulfilled,
        (
          state,
          {
            payload: {
              data: { success = false, filters = [] } = {},
              searchType = null,
              s,
            } = {},
          },
        ) => {
          state.filtersLoading = false;
          if (success) {
            state.searchFilters[searchType] = [...filters];
          }
        },
      )
      .addCase(fetchFilters.rejected, (state) => {
        state.filtersLoading = false;
      });
  },
});

export const {
  clearSearch,
  resetCurrentFilters,
  setAppliedFilters,
  setQueryValue,
  setSearchType,
  setSearchResultsType,
  changePage,
  updateFilters,
  setSortBy,
  uncheckFilter,
  setSearchPeriod,
  setSelectedPersonFilter,
  setSearchModelType,
  setModelTypeSearchFilter,
  setModelTypeAppliedFilter,
  clearModelTypeAppliedFilters,
  setCurrentInputQuery,
  updateModelTypeFilters,
  clearAllModelTypeAppliedFilters,
} = searchSlice.actions;

export default searchSlice.reducer;
